import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, MapPin, Clock, Building2 } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Empty from "@/components/Empty";

const CATEGORIES = [
  "Academic", "Music Education", "Technology", "Marketing", "Sales & Growth",
  "Business Development", "Operations", "Human Resources", "Creative Media", "Internships",
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="Careers at RCS"
        title="Build the Future of Learning With Us."
        lede="At Roy Career Solutions Private Limited, we are building education brands that combine learning, technology and creativity — and we are looking for people who want to build them with us."
      />

      <section className="py-24 lg:py-32" data-testid="open-roles">
        <div className="container-rcs">
          <Section eyebrow="Open Roles" title="Opportunities across our brands." />
          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="career-categories">
            {CATEGORIES.map((c) => (
              <span key={c} className="rounded-full border border-navy-900/15 bg-white px-4 py-1.5 text-xs font-semibold text-navy-900">
                {c}
              </span>
            ))}
          </div>

          <div className="mt-12">
            {jobs.length === 0 ? (
              <Empty
                icon={Briefcase}
                title="New roles are being prepared"
                text="We do not have open positions listed right now, but we are always interested in hearing from exceptional educators, technologists and builders. Send us a note through the contact page."
                testid="jobs-empty"
              />
            ) : (
              <div className="space-y-5">
                {jobs.map((j, i) => (
                  <Reveal key={j.id} delay={i * 0.06}>
                    <Link
                      to={`/careers/${j.slug}`}
                      data-testid={`job-card-${j.slug}`}
                      className="card-premium flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <h3 className="font-serif text-2xl text-navy-900">{j.title}</h3>
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                          {j.department && (
                            <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-gold-dark" aria-hidden />{j.department}</span>
                          )}
                          {j.brand && (
                            <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gold-dark" aria-hidden />{j.brand}</span>
                          )}
                          {j.location && (
                            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold-dark" aria-hidden />{j.location}{j.workMode ? ` · ${j.workMode}` : ""}</span>
                          )}
                          {j.employmentType && (
                            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold-dark" aria-hidden />{j.employmentType}</span>
                          )}
                        </div>
                      </div>
                      <span className="btn-gold shrink-0 !px-6 !py-2.5 !text-xs">
                        View & Apply <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-24" data-testid="life-at-rcs">
        <div className="container-rcs">
          <Section
            dark
            eyebrow="Life at RCS"
            title="A team that learns while helping others learn."
            lede="Team meetings, virtual events, workshops, celebrations and employee highlights — glimpses of life inside RCS will be shared here as our team grows."
          />
        </div>
      </section>
    </div>
  );
}
